import React, { useState, useEffect, useCallback } from 'react';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { PublicClientApplication, InteractionStatus } from '@azure/msal-browser';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Grid
} from '@material-ui/core';
import { 
  Refresh, 
  Sync, 
  Group, 
  Error 
} from '@material-ui/icons';
import { MeetingFilters } from './components/MeetingFilters';
import { MeetingsTable } from './components/MeetingsTable';
import { MeetingStats } from './components/MeetingStats';
import { CalendarView } from './components/CalendarView';
import { LoginPage } from './components/LoginPage';
import { getCalendarEvents, getUserPhoto, getOrganizerJoinStatus } from './services/graphService';
import { convertEventToMeeting, getMeetingStatus } from './utils/meetingUtils';
import { loginRequest } from './config/msalConfig';
import './Teams.css';

// MSAL configuration for Teams integration
const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_AZURE_CLIENT_ID || 'your-client-id-here',
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_AZURE_TENANT_ID || 'common'}`,
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

const msalInstance = new PublicClientApplication(msalConfig);

function TeamsContent() {
  const { instance, inProgress } = useMsal();
  const [meetings, setMeetings] = useState([]);
  const [filteredMeetings, setFilteredMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    accountCount: 0,
  });
  const [currentPage, setCurrentPage] = useState(() => {
    // Determine page based on current path
    const path = window.location.pathname;
    if (path.includes('calendario')) {
      return 'calendar';
    }
    return 'all-meetings';
  });

  // Initialize filters with default dates (today and tomorrow)
  const getDefaultDates = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const formatDateForInput = (date) => {
      return date.toISOString().split('T')[0];
    };
    
    return {
      dateStart: formatDateForInput(today),
      dateEnd: formatDateForInput(tomorrow),
    };
  };

  const [filters, setFilters] = useState(() => {
    const defaultDates = getDefaultDates();
    return {
      dateStart: defaultDates.dateStart,
      dateEnd: defaultDates.dateEnd,
      organizer: '',
      status: '',
      joined: '',
    };
  });

  // Handle route-based navigation
  useEffect(() => {
    const handleLocationChange = () => {
      const path = window.location.pathname;
      if (path.includes('calendario')) {
        setCurrentPage('calendar');
      } else {
        setCurrentPage('all-meetings');
      }
    };

    // Set initial page from path
    handleLocationChange();

    // Listen for navigation changes
    window.addEventListener('popstate', handleLocationChange);
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, []);

  // Check if MSAL is initialized
  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      setIsInitialized(true);
    }
  }, [inProgress]);

  // Check accounts from instance directly
  useEffect(() => {
    if (!isInitialized) return;

    const checkAccounts = () => {
      try {
        const allAccounts = instance.getAllAccounts();
        const accountCount = allAccounts.length;
        const authenticated = accountCount > 0;
        
        if (authenticated !== authState.isAuthenticated || accountCount !== authState.accountCount) {
          setAuthState({
            isAuthenticated: authenticated,
            accountCount,
          });
        }
      } catch (err) {
        console.error('Error checking accounts:', err);
      }
    };

    checkAccounts();
    
    if (!authState.isAuthenticated) {
      const interval = setInterval(() => {
        checkAccounts();
      }, 1000);

      const timeout = setTimeout(() => {
        clearInterval(interval);
      }, 5000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
  }, [instance, isInitialized, authState.isAuthenticated, authState.accountCount]);

  // Listen for account update events
  useEffect(() => {
    const handleAccountsUpdate = () => {
      const allAccounts = instance.getAllAccounts();
      
      if (allAccounts.length > 0) {
        setAuthState({
          isAuthenticated: true,
          accountCount: allAccounts.length,
        });
      } else {
        setTimeout(() => {
          const retryAccounts = instance.getAllAccounts();
          if (retryAccounts.length > 0) {
            setAuthState({
              isAuthenticated: true,
              accountCount: retryAccounts.length,
            });
          }
        }, 1000);
      }
    };

    window.addEventListener('accounts-updated', handleAccountsUpdate);
    return () => {
      window.removeEventListener('accounts-updated', handleAccountsUpdate);
    };
  }, [instance]);

  const hasAuthenticated = authState.isAuthenticated;

  // Get access token and fetch calendar events
  const fetchMeetings = useCallback(async () => {
    const allAccounts = instance.getAllAccounts();
    if (allAccounts.length === 0) {
      console.log('No accounts available for fetching meetings');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: allAccounts[0],
      });

      const accessToken = response.accessToken;
      const currentUserEmail = allAccounts[0].username || '';

      let startDateTime;
      let endDateTime;

      if (filters.dateStart && filters.dateEnd) {
        const startDate = new Date(filters.dateStart + 'T00:00:00');
        const endDate = new Date(filters.dateEnd + 'T23:59:59.999');
        
        startDateTime = startDate.toISOString();
        endDateTime = endDate.toISOString();
      } else {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        startDateTime = today.toISOString();
        endDateTime = tomorrow.toISOString();
      }

      const events = await getCalendarEvents(accessToken, startDateTime, endDateTime);

      const meetingsPromises = events.map(async (event) => {
        const organizerEmail = event.organizer.emailAddress.address;
        const organizerHasJoined = await getOrganizerJoinStatus(event, currentUserEmail, accessToken);
        
        let organizerPhotoUrl = null;
        // getUserPhoto maneja todos los errores internamente y devuelve null si no hay foto
        organizerPhotoUrl = await getUserPhoto(accessToken, organizerEmail);

        return convertEventToMeeting(event, organizerPhotoUrl, organizerHasJoined);
      });

      const meetingsData = await Promise.all(meetingsPromises);
      setMeetings(meetingsData);
      setFilteredMeetings(meetingsData);
    } catch (err) {
      console.error('Error fetching meetings:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch meetings');
    } finally {
      setLoading(false);
    }
  }, [instance, filters.dateStart, filters.dateEnd]);

  // Fetch meetings when authenticated or when date filters change
  useEffect(() => {
    if (hasAuthenticated && inProgress === InteractionStatus.None && isInitialized) {
      fetchMeetings();
    }
  }, [hasAuthenticated, inProgress, isInitialized, fetchMeetings]);

  // Auto-refresh meetings every 2 minutes if there are ongoing meetings
  useEffect(() => {
    if (!hasAuthenticated || loading) return;

    const hasOngoingMeetings = meetings.some((m) => {
      const now = new Date();
      const start = new Date(m.scheduledStart);
      const end = new Date(m.scheduledEnd);
      return now >= start && now < end;
    });

    if (hasOngoingMeetings) {
      const refreshInterval = setInterval(() => {
        console.log('Auto-refreshing meetings (ongoing meeting detected)');
        fetchMeetings();
      }, 120000);

      return () => clearInterval(refreshInterval);
    }
  }, [hasAuthenticated, meetings, loading, fetchMeetings]);

  // Apply filters when meetings or filters change
  useEffect(() => {
    let filtered = [...meetings];

    if (filters.organizer) {
      filtered = filtered.filter(
        (m) => m.organizer.name === filters.organizer
      );
    }

    if (filters.status) {
      filtered = filtered.filter((m) => {
        const currentStatus = getMeetingStatus(m.scheduledStart, m.scheduledEnd, m.timeZone);
        return currentStatus === filters.status;
      });
    }

    if (filters.dateStart) {
      const startDate = new Date(filters.dateStart + 'T00:00:00');
      filtered = filtered.filter((m) => {
        let meetingDate;
        const hasTz = m.scheduledStart.endsWith('Z') || m.scheduledStart.match(/[-+]\d{2}:?\d{2}$/);
        
        if (!hasTz && m.timeZone === 'UTC') {
          const cleanDate = m.scheduledStart.replace(/\.\d+$/, '') + 'Z';
          meetingDate = new Date(cleanDate);
        } else {
          meetingDate = new Date(m.scheduledStart);
        }
        
        return meetingDate >= startDate;
      });
    }

    if (filters.dateEnd) {
      const endDate = new Date(filters.dateEnd + 'T23:59:59.999');
      filtered = filtered.filter((m) => {
        let meetingDate;
        const hasTz = m.scheduledStart.endsWith('Z') || m.scheduledStart.match(/[-+]\d{2}:?\d{2}$/);
        
        if (!hasTz && m.timeZone === 'UTC') {
          const cleanDate = m.scheduledStart.replace(/\.\d+$/, '') + 'Z';
          meetingDate = new Date(cleanDate);
        } else {
          meetingDate = new Date(m.scheduledStart);
        }
        
        return meetingDate <= endDate;
      });
    }

    if (filters.joined) {
      filtered = filtered.filter((m) => {
        if (filters.joined === 'joined') {
          return m.organizer.hasJoined;
        } else if (filters.joined === 'not_joined') {
          return !m.organizer.hasJoined;
        }
        return true;
      });
    }

    setFilteredMeetings(filtered);
  }, [meetings, filters]);

  const organizers = Array.from(
    new Set(meetings.map((m) => m.organizer.name))
  ).sort();

  const handleMeetingClick = (meetingId) => {
    const meeting = meetings.find((m) => m.id === meetingId);
    if (meeting?.joinUrl) {
      window.open(meeting.joinUrl, '_blank');
    } else {
      console.log('Meeting details:', meeting);
    }
  };

  if (!isInitialized || inProgress !== InteractionStatus.None) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Box textAlign="center">
          <Box mb={2}>
            <Refresh className="teams-loading-icon" style={{ fontSize: 48, color: '#667eea' }} />
          </Box>
          <Typography variant="h6" color="textSecondary">
            Cargando...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!hasAuthenticated) {
    return <LoginPage />;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Box textAlign="center">
          <Box mb={2}>
            <Sync className="teams-loading-icon" style={{ fontSize: 48, color: '#667eea' }} />
          </Box>
          <Typography variant="h6" color="textSecondary">
            Cargando reuniones...
          </Typography>
        </Box>
      </Box>
    );
  }

  const renderPageContent = () => {
    switch (currentPage) {
      case 'calendar':
        return (
          <CalendarView
            meetings={filteredMeetings}
            onMeetingClick={handleMeetingClick}
          />
        );
      case 'all-meetings':
      default:
        return (
          <>
            <MeetingStats meetings={filteredMeetings} />
            <MeetingFilters
              filters={filters}
              onFilterChange={setFilters}
              organizers={organizers}
            />
            <MeetingsTable meetings={filteredMeetings} onMeetingClick={handleMeetingClick} />
          </>
        );
    }
  };

  const getPageTitle = () => {
    switch (currentPage) {
      case 'calendar':
        return 'Calendario Teams';
      case 'all-meetings':
      default:
        return 'Reuniones Teams';
    }
  };

  return (
    <Box className="container-fluid" style={{ padding: 24 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card style={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
            <Box 
              style={{ 
                padding: '24px 32px', 
                borderBottom: '1px solid #e0e0e0',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: '12px 12px 0 0'
              }}
            >
              <Box display="flex" alignItems="center">
                <Group style={{ marginRight: 12, fontSize: 28 }} />
                <Typography variant="h4" component="h1" style={{ fontWeight: 600 }}>
                  {getPageTitle()}
                </Typography>
              </Box>
            </Box>
            <CardContent style={{ padding: 32 }}>
              {error && (
                <Box mb={3}>
                  <Card style={{ backgroundColor: '#ffebee', border: '1px solid #f44336' }}>
                    <CardContent>
                      <Box display="flex" alignItems="center">
                        <Error style={{ color: '#f44336', marginRight: 8 }} />
                        <Typography color="error">
                          Error: {error}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              )}
              {renderPageContent()}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

function Teams() {
  return (
    <MsalProvider instance={msalInstance}>
      <TeamsContent />
    </MsalProvider>
  );
}

export default Teams;